/**
 * Self-contained demo data for the Supplier 360 cockpit.
 *
 * This module ships representative mock data so the cockpit is fully
 * functional without any backend dependency. It is intentionally typed
 * and structured so it can later be swapped for real API responses.
 */

export type RiskLevel = 'low' | 'medium' | 'high';
export type ContractStatus = 'active' | 'expiring' | 'expired' | 'draft';
export type OpportunityStatus = 'identified' | 'in_progress' | 'realized';
export type NewsSentiment = 'positive' | 'neutral' | 'negative';

export type Supplier360Transaction = {
    id: string;
    date: string;            // ISO date
    description: string;
    category: string;
    poNumber: string;
    amount: number;          // in supplier currency
    status: 'paid' | 'open' | 'overdue';
};

export type Supplier360Contract = {
    id: string;
    name: string;
    type: string;
    status: ContractStatus;
    value: number;
    startDate: string;       // ISO date
    endDate: string;         // ISO date
    owner: string;
    autoRenew: boolean;
};

export type Supplier360News = {
    id: string;
    date: string;            // ISO date
    headline: string;
    source: string;
    sentiment: NewsSentiment;
    summary: string;
};

export type Supplier360Opportunity = {
    id: string;
    title: string;
    type: string;
    status: OpportunityStatus;
    estimatedSavings: number;
    confidence: number;      // 0..1
    description: string;
};

export type Supplier360Risk = {
    id: string;
    category: string;        // e.g. Financial, Compliance, Geopolitical
    level: RiskLevel;
    title: string;
    detail: string;
    detectedOn: string;      // ISO date
};

export type Supplier360 = {
    id: string;
    name: string;
    logoColor: string;       // accent color used for the avatar
    category: string;
    subCategories: string[];
    country: string;
    region: string;
    currency: string;
    relationshipSince: number; // year
    status: 'preferred' | 'approved' | 'under_review';
    annualSpend: number;
    spendTrendPct: number;   // YoY change, e.g. 0.12 = +12%
    spendShareOfCategory: number; // 0..1
    transactionCount: number;
    activeContracts: number;
    onTimeDeliveryPct: number;   // 0..1
    qualityScore: number;        // 0..100
    riskScore: number;           // 0..100 (higher = riskier)
    esgScore: number;            // 0..100
    paymentTerms: string;
    primaryContact: {name: string; role: string; email: string};
    transactions: Supplier360Transaction[];
    contracts: Supplier360Contract[];
    news: Supplier360News[];
    opportunities: Supplier360Opportunity[];
    risks: Supplier360Risk[];
};

const SUPPLIERS: Supplier360[] = [
    {
        id: 'sup-acme-steel',
        name: 'Acme Steel Works',
        logoColor: '#478FFA',
        category: 'Raw Materials',
        subCategories: ['Steel', 'Metal Fabrication'],
        country: 'Germany',
        region: 'EMEA',
        currency: 'EUR',
        relationshipSince: 2014,
        status: 'preferred',
        annualSpend: 12_450_000,
        spendTrendPct: 0.08,
        spendShareOfCategory: 0.34,
        transactionCount: 1284,
        activeContracts: 3,
        onTimeDeliveryPct: 0.96,
        qualityScore: 92,
        riskScore: 24,
        esgScore: 78,
        paymentTerms: 'Net 45',
        primaryContact: {name: 'Klaus Berger', role: 'Key Account Manager', email: 'k.berger@acmesteel.de'},
        transactions: [
            {id: 't1', date: '2026-05-28', description: 'Hot-rolled steel coils', category: 'Steel', poNumber: 'PO-88412', amount: 482_300, status: 'paid'},
            {id: 't2', date: '2026-05-14', description: 'Galvanized sheet metal', category: 'Steel', poNumber: 'PO-88107', amount: 215_900, status: 'paid'},
            {id: 't3', date: '2026-04-30', description: 'Custom fabricated brackets', category: 'Metal Fabrication', poNumber: 'PO-87650', amount: 96_400, status: 'open'},
            {id: 't4', date: '2026-04-12', description: 'Stainless steel tubing', category: 'Steel', poNumber: 'PO-87231', amount: 158_750, status: 'paid'},
            {id: 't5', date: '2026-03-22', description: 'Steel reinforcement bars', category: 'Steel', poNumber: 'PO-86540', amount: 327_000, status: 'overdue'},
        ],
        contracts: [
            {id: 'c1', name: 'Master Supply Agreement', type: 'Framework', status: 'active', value: 9_500_000, startDate: '2024-01-01', endDate: '2026-12-31', owner: 'Procurement EMEA', autoRenew: true},
            {id: 'c2', name: 'Logistics Addendum', type: 'Service', status: 'expiring', value: 640_000, startDate: '2025-07-01', endDate: '2026-06-30', owner: 'Supply Chain', autoRenew: false},
            {id: 'c3', name: 'Quality SLA', type: 'SLA', status: 'active', value: 0, startDate: '2024-01-01', endDate: '2026-12-31', owner: 'Quality Assurance', autoRenew: true},
        ],
        news: [
            {id: 'n1', date: '2026-05-20', headline: 'Acme Steel announces €200M green steel plant', source: 'Reuters', sentiment: 'positive', summary: 'New low-carbon production facility expected online in 2028, supporting decarbonization targets.'},
            {id: 'n2', date: '2026-04-02', headline: 'EU carbon border levy may raise steel input costs', source: 'Bloomberg', sentiment: 'negative', summary: 'CBAM phase-in could increase landed cost of imported inputs over the next two years.'},
        ],
        opportunities: [
            {id: 'o1', title: 'Consolidate fabrication volume', type: 'Spend Consolidation', status: 'identified', estimatedSavings: 410_000, confidence: 0.72, description: 'Shift fragmented fabrication POs to the framework agreement for volume discounts.'},
            {id: 'o2', title: 'Renegotiate payment terms to Net 60', type: 'Working Capital', status: 'in_progress', estimatedSavings: 180_000, confidence: 0.6, description: 'Extend payment terms to improve days-payable-outstanding.'},
        ],
        risks: [
            {id: 'r1', category: 'Geopolitical', level: 'medium', title: 'Energy price exposure', detail: 'High exposure to European energy markets affecting production cost stability.', detectedOn: '2026-03-15'},
            {id: 'r2', category: 'Concentration', level: 'medium', title: 'Single-source steel coils', detail: '34% of category spend concentrated with this supplier.', detectedOn: '2026-02-01'},
        ],
    },
    {
        id: 'sup-globex-logistics',
        name: 'Globex Logistics',
        logoColor: '#08A57A',
        category: 'Logistics & Freight',
        subCategories: ['Ocean Freight', 'Warehousing'],
        country: 'Netherlands',
        region: 'EMEA',
        currency: 'EUR',
        relationshipSince: 2018,
        status: 'approved',
        annualSpend: 7_820_000,
        spendTrendPct: -0.04,
        spendShareOfCategory: 0.21,
        transactionCount: 3120,
        activeContracts: 2,
        onTimeDeliveryPct: 0.89,
        qualityScore: 84,
        riskScore: 41,
        esgScore: 65,
        paymentTerms: 'Net 30',
        primaryContact: {name: 'Anouk de Vries', role: 'Account Director', email: 'anouk.devries@globex.nl'},
        transactions: [
            {id: 't1', date: '2026-05-30', description: 'Ocean freight FCL Asia-EU', category: 'Ocean Freight', poNumber: 'PO-91220', amount: 184_500, status: 'open'},
            {id: 't2', date: '2026-05-18', description: 'Warehouse storage Rotterdam', category: 'Warehousing', poNumber: 'PO-90980', amount: 62_300, status: 'paid'},
            {id: 't3', date: '2026-05-02', description: 'Last-mile distribution', category: 'Distribution', poNumber: 'PO-90611', amount: 38_900, status: 'paid'},
            {id: 't4', date: '2026-04-19', description: 'Customs brokerage', category: 'Brokerage', poNumber: 'PO-90122', amount: 14_200, status: 'paid'},
        ],
        contracts: [
            {id: 'c1', name: 'Freight Rate Card 2026', type: 'Framework', status: 'active', value: 5_400_000, startDate: '2026-01-01', endDate: '2026-12-31', owner: 'Logistics', autoRenew: false},
            {id: 'c2', name: 'Warehousing Lease', type: 'Lease', status: 'expiring', value: 1_200_000, startDate: '2023-06-01', endDate: '2026-07-31', owner: 'Operations', autoRenew: false},
        ],
        news: [
            {id: 'n1', date: '2026-05-11', headline: 'Red Sea disruptions extend Asia-Europe transit times', source: 'Lloyd\'s List', sentiment: 'negative', summary: 'Re-routing around Africa continues to add 10-14 days to ocean freight lead times.'},
            {id: 'n2', date: '2026-03-28', headline: 'Globex invests in electric drayage fleet', source: 'Supply Chain Dive', sentiment: 'positive', summary: 'Fleet electrification expected to lower scope-3 emissions for customers.'},
        ],
        opportunities: [
            {id: 'o1', title: 'Mode shift to rail for EU lanes', type: 'Cost & ESG', status: 'identified', estimatedSavings: 290_000, confidence: 0.55, description: 'Move suitable lanes from road to rail to cut cost and emissions.'},
        ],
        risks: [
            {id: 'r1', category: 'Operational', level: 'high', title: 'On-time delivery below target', detail: 'OTD at 89% vs 95% target, driven by ocean freight delays.', detectedOn: '2026-05-01'},
            {id: 'r2', category: 'Financial', level: 'medium', title: 'Fuel surcharge volatility', detail: 'Exposure to bunker fuel surcharges affecting billed amounts.', detectedOn: '2026-04-10'},
        ],
    },
    {
        id: 'sup-initech-it',
        name: 'Initech IT Services',
        logoColor: '#8B5CF6',
        category: 'IT & Software',
        subCategories: ['SaaS Licenses', 'Managed Services'],
        country: 'United States',
        region: 'Americas',
        currency: 'USD',
        relationshipSince: 2020,
        status: 'approved',
        annualSpend: 4_310_000,
        spendTrendPct: 0.22,
        spendShareOfCategory: 0.18,
        transactionCount: 412,
        activeContracts: 4,
        onTimeDeliveryPct: 0.98,
        qualityScore: 88,
        riskScore: 33,
        esgScore: 71,
        paymentTerms: 'Net 30',
        primaryContact: {name: 'Marcus Lee', role: 'Customer Success Lead', email: 'mlee@initech.com'},
        transactions: [
            {id: 't1', date: '2026-06-01', description: 'Annual SaaS platform license', category: 'SaaS Licenses', poNumber: 'PO-IT-2201', amount: 980_000, status: 'open'},
            {id: 't2', date: '2026-05-15', description: 'Managed security services Q2', category: 'Managed Services', poNumber: 'PO-IT-2188', amount: 145_000, status: 'paid'},
            {id: 't3', date: '2026-04-15', description: 'Cloud infrastructure usage', category: 'Cloud', poNumber: 'PO-IT-2150', amount: 88_500, status: 'paid'},
        ],
        contracts: [
            {id: 'c1', name: 'Enterprise License Agreement', type: 'Framework', status: 'active', value: 3_600_000, startDate: '2025-01-01', endDate: '2027-12-31', owner: 'IT Procurement', autoRenew: true},
            {id: 'c2', name: 'Managed Services Agreement', type: 'Service', status: 'active', value: 720_000, startDate: '2025-04-01', endDate: '2026-03-31', owner: 'IT Operations', autoRenew: true},
            {id: 'c3', name: 'Data Processing Agreement', type: 'Compliance', status: 'active', value: 0, startDate: '2025-01-01', endDate: '2027-12-31', owner: 'Legal', autoRenew: true},
            {id: 'c4', name: 'Pilot SOW - AI Add-on', type: 'SOW', status: 'draft', value: 120_000, startDate: '2026-07-01', endDate: '2026-12-31', owner: 'Innovation', autoRenew: false},
        ],
        news: [
            {id: 'n1', date: '2026-05-25', headline: 'Initech raises Series D, expands EU data centers', source: 'TechCrunch', sentiment: 'positive', summary: 'New funding supports EU data residency, reducing compliance risk for European customers.'},
            {id: 'n2', date: '2026-04-08', headline: 'Initech discloses minor security incident', source: 'The Register', sentiment: 'negative', summary: 'Contained incident with no customer data exfiltration reported; remediation completed.'},
        ],
        opportunities: [
            {id: 'o1', title: 'License rationalization', type: 'Demand Management', status: 'in_progress', estimatedSavings: 215_000, confidence: 0.8, description: 'Reclaim unused seats and right-size license tiers based on actual usage.'},
            {id: 'o2', title: 'Multi-year commit discount', type: 'Negotiation', status: 'identified', estimatedSavings: 340_000, confidence: 0.65, description: 'Lock a 3-year term in exchange for an additional volume discount.'},
        ],
        risks: [
            {id: 'r1', category: 'Compliance', level: 'medium', title: 'Recent security incident', detail: 'Monitor remediation and SOC 2 report renewal.', detectedOn: '2026-04-08'},
            {id: 'r2', category: 'Financial', level: 'low', title: 'High spend growth', detail: 'Spend up 22% YoY; validate against budget.', detectedOn: '2026-03-01'},
        ],
    },
    {
        id: 'sup-soylent-foods',
        name: 'Soylent Food Ingredients',
        logoColor: '#FAA915',
        category: 'Food & Ingredients',
        subCategories: ['Additives', 'Packaging'],
        country: 'Brazil',
        region: 'Americas',
        currency: 'USD',
        relationshipSince: 2016,
        status: 'under_review',
        annualSpend: 5_640_000,
        spendTrendPct: 0.15,
        spendShareOfCategory: 0.27,
        transactionCount: 902,
        activeContracts: 2,
        onTimeDeliveryPct: 0.91,
        qualityScore: 79,
        riskScore: 58,
        esgScore: 52,
        paymentTerms: 'Net 60',
        primaryContact: {name: 'Beatriz Almeida', role: 'Export Manager', email: 'b.almeida@soylent.com.br'},
        transactions: [
            {id: 't1', date: '2026-05-27', description: 'Natural flavor concentrates', category: 'Additives', poNumber: 'PO-SF-7720', amount: 312_000, status: 'paid'},
            {id: 't2', date: '2026-05-09', description: 'Recyclable packaging film', category: 'Packaging', poNumber: 'PO-SF-7688', amount: 87_400, status: 'open'},
            {id: 't3', date: '2026-04-21', description: 'Preservative blend', category: 'Additives', poNumber: 'PO-SF-7610', amount: 154_900, status: 'overdue'},
        ],
        contracts: [
            {id: 'c1', name: 'Annual Supply Contract', type: 'Framework', status: 'active', value: 4_200_000, startDate: '2026-01-01', endDate: '2026-12-31', owner: 'Procurement LATAM', autoRenew: false},
            {id: 'c2', name: 'Food Safety Compliance', type: 'Compliance', status: 'expiring', value: 0, startDate: '2024-06-01', endDate: '2026-06-30', owner: 'Quality Assurance', autoRenew: false},
        ],
        news: [
            {id: 'n1', date: '2026-05-16', headline: 'Drought pressures Brazilian agricultural output', source: 'Financial Times', sentiment: 'negative', summary: 'Reduced harvests may tighten ingredient supply and push prices higher.'},
            {id: 'n2', date: '2026-02-19', headline: 'Soylent earns sustainable sourcing certification', source: 'Food Dive', sentiment: 'positive', summary: 'Certification improves ESG profile for downstream buyers.'},
        ],
        opportunities: [
            {id: 'o1', title: 'Dual-source critical additives', type: 'Risk Mitigation', status: 'identified', estimatedSavings: 0, confidence: 0.7, description: 'Qualify a second supplier to reduce single-source exposure on additives.'},
            {id: 'o2', title: 'Packaging spec harmonization', type: 'Standardization', status: 'identified', estimatedSavings: 130_000, confidence: 0.5, description: 'Standardize packaging specs to reduce SKU complexity and cost.'},
        ],
        risks: [
            {id: 'r1', category: 'Compliance', level: 'high', title: 'Food safety cert expiring', detail: 'Compliance certificate expires 2026-06-30; renewal not yet confirmed.', detectedOn: '2026-05-05'},
            {id: 'r2', category: 'Geopolitical', level: 'high', title: 'Climate-driven supply risk', detail: 'Regional drought threatens raw ingredient availability.', detectedOn: '2026-05-16'},
            {id: 'r3', category: 'Financial', level: 'medium', title: 'Overdue payments backlog', detail: 'Outstanding overdue invoices may strain the relationship.', detectedOn: '2026-04-25'},
        ],
    },
    {
        id: 'sup-umbrella-pharma',
        name: 'Umbrella Pharma Supplies',
        logoColor: '#FF3435',
        category: 'Lab & Pharma',
        subCategories: ['Reagents', 'Lab Equipment'],
        country: 'Switzerland',
        region: 'EMEA',
        currency: 'CHF',
        relationshipSince: 2012,
        status: 'preferred',
        annualSpend: 9_120_000,
        spendTrendPct: 0.05,
        spendShareOfCategory: 0.44,
        transactionCount: 678,
        activeContracts: 3,
        onTimeDeliveryPct: 0.97,
        qualityScore: 95,
        riskScore: 19,
        esgScore: 83,
        paymentTerms: 'Net 45',
        primaryContact: {name: 'Dr. Sophie Müller', role: 'Strategic Account Lead', email: 's.mueller@umbrella-pharma.ch'},
        transactions: [
            {id: 't1', date: '2026-05-29', description: 'High-purity reagents batch', category: 'Reagents', poNumber: 'PO-UP-3301', amount: 421_000, status: 'paid'},
            {id: 't2', date: '2026-05-12', description: 'Lab centrifuge units', category: 'Lab Equipment', poNumber: 'PO-UP-3270', amount: 256_500, status: 'paid'},
            {id: 't3', date: '2026-04-28', description: 'Calibration service', category: 'Service', poNumber: 'PO-UP-3211', amount: 42_000, status: 'paid'},
        ],
        contracts: [
            {id: 'c1', name: 'Strategic Partnership Agreement', type: 'Framework', status: 'active', value: 7_800_000, startDate: '2024-01-01', endDate: '2027-12-31', owner: 'Procurement EMEA', autoRenew: true},
            {id: 'c2', name: 'Equipment Maintenance', type: 'Service', status: 'active', value: 540_000, startDate: '2025-01-01', endDate: '2026-12-31', owner: 'Lab Operations', autoRenew: true},
            {id: 'c3', name: 'GMP Quality Agreement', type: 'Compliance', status: 'active', value: 0, startDate: '2024-01-01', endDate: '2027-12-31', owner: 'Regulatory', autoRenew: true},
        ],
        news: [
            {id: 'n1', date: '2026-05-22', headline: 'Umbrella Pharma expands sustainable reagent line', source: 'C&EN', sentiment: 'positive', summary: 'Greener reagent portfolio supports lab decarbonization goals.'},
        ],
        opportunities: [
            {id: 'o1', title: 'Bundle equipment + maintenance', type: 'Bundling', status: 'realized', estimatedSavings: 95_000, confidence: 0.9, description: 'Bundling delivered a realized discount on maintenance contracts.'},
        ],
        risks: [
            {id: 'r1', category: 'Concentration', level: 'medium', title: 'High category concentration', detail: '44% of category spend with a single supplier.', detectedOn: '2026-01-20'},
        ],
    },
];

export function getSupplier360List(): Supplier360[] {
    return SUPPLIERS;
}

export function getSupplier360ById(id: string): Supplier360 | undefined {
    return SUPPLIERS.find(s => s.id === id);
}
